import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import useOutsideClick from "hooks/click-outside";
import { CORE_CONCEPT_TYPE } from "utils/helpers/enums/shared.enum";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selected?: string | null;
};

const AcknowledgedPopup: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  selected,
}) => {
  const modalRef = useOutsideClick(onClose);

  if (!isOpen) return null;

  const renderContent = () => {
    if (!selected) {
      return (
        <>
          <div className="text-lightBlack text-center">
            <p className="mb-4">
              Every child is unique, and so is the way they absorb, process, and
              apply knowledge. Understanding your child's preferred learning
              style(s) can make their experiences more engaging, meaningful, and
              effective—both in the classroom and in real-world learning
              environments.
            </p>

            <h3 className="font-semibold text-lightBlackText mb-2">
              Why is this important?
            </h3>
            <ul className="space-y-1">
              <li className="">
                ✅ Helps parents choose Learn Local experiences that align with
                their child's strengths.
              </li>
              <li className="">
                ✅ Encourages kids to lean into their natural way of learning
                while also expanding into new areas.
              </li>
              <li className="">
                ✅ Supports better learning retention by using personalized
                approaches at home and in the community.
              </li>
              <li className="">
                ✨ Every child learns in all 7 ways! This questionnaire helps
                identify which styles they prefer most so families can focus on
                those strengths while encouraging development in other areas.
              </li>
            </ul>
          </div>
        </>
      );
    }

    if (selected === CORE_CONCEPT_TYPE.APPROPRIATE_FOR_AGE_LEVEL) {
      return (
        <>
          <h3 className="font-semibold text-lightBlackText mb-2">
            Advanced Learning Skills
          </h3>
          <p className="mb-4">
            Designed for learners ready to engage in higher-level thinking and
            problem-solving. This assessment focuses on skills like critical
            thinking, planning, communication, innovation, and leadership. It's
            ideal for older children, teens, or those demonstrating advanced
            cognitive abilities who are ready to apply knowledge in real-world,
            meaningful ways.
          </p>
        </>
      );
    }

    if (selected === CORE_CONCEPT_TYPE.EXCEPTION_LEARNER) {
      return (
        <>
          <h3 className="font-semibold text-lightBlackText mb-2">
            Foundational Learning Skills
          </h3>
          <p className="mb-4">
            Focused on the early stages of development, this assessment supports
            learners in building essential skills across social, emotional,
            cognitive, and motor domains. It's ideal for younger children or
            those who need support with core developmental milestones like
            emotional regulation, empathy, coordination, and basic executive
            functioning.
          </p>
        </>
      );
    }
  };

  return (
    <div style={{ zIndex: 'calc(var(--z-modal) + 1)' }} className="fixed  inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300">
      <div
        ref={modalRef}
        className="relative bg-white px-6 py-8 rounded-lg shadow-lg w-[90%] sm:w-[380px] transform transition-transform duration-300"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition"
        >
          <AiOutlineClose size={20} />
        </button>

        <h2 className="text-xl text-center text-blackie font-bold mb-4">
          Skill Level Assessment
        </h2>

        <div className="text-lightBlack text-center">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AcknowledgedPopup;
